import cn from 'classnames';
import { capitalize } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Card, IconTooltip, ModalUI, Tag, ThemeType } from 'sonata-react-components';

import './activationAreasModal.scss';

type Props = {
  isModalOpened: boolean;
  onModalClose: () => void;
  onModalSave?: (selectedAction: ActivationAreasAction) => void;
  theme?: ThemeType;
};

type State = {
  selectedAction?: ActivationAreasAction;
};

const ACTIVATION_AREAS_ACTIONS = [
  'cartographySelector',
  'addressRadiusFile',
  'areasFromPOIs',
  'polygonArea',
  'circularArea'
] as const;

export type ActivationAreasAction = (typeof ACTIVATION_AREAS_ACTIONS)[number];

const ACTIVATION_ACTION_ICONS: Record<ActivationAreasAction, string> = {
  addressRadiusFile: 'fa-solid fa-file-import',
  areasFromPOIs: 'fa-kit-duotone fa-address-radius-file',
  cartographySelector: 'fa-solid fa-location-dot',
  circularArea: 'fa-solid fa-circle-dashed',
  polygonArea: 'fa-solid fa-draw-polygon'
};

export function ActivationAreasModal({ isModalOpened, onModalClose, onModalSave }: Props) {
  const modalRef = useRef<any>(null);
  const { formatMessage } = useIntl();

  const [{ selectedAction }, setState] = useState<State>({});

  useEffect(() => {
    if (isModalOpened) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isModalOpened]);

  const itemsConfig = ACTIVATION_AREAS_ACTIONS.map(option => ({
    bottomBarSelected: true,
    description: formatMessage({
      defaultMessage: `${capitalize(option)} description`,
      id: `actionToolsPanel.activationAreasModal.action.${option}.description`
    }),
    iconDefault: ACTIVATION_ACTION_ICONS[option],
    iconSelected: 'fas fa-check-circle',
    label: formatMessage({
      defaultMessage: `${capitalize(option)} label`,
      id: `actionToolsPanel.activationAreasModal.action.${option}.label`
    }),
    tag: formatMessage({
      defaultMessage: ['polygonArea', 'circularArea'].includes(option) ? 'Single Area' : 'Multiple Areas',
      id: ['polygonArea', 'circularArea'].includes(option)
        ? `actionToolsPanel.activationAreasModal.tag.single`
        : `actionToolsPanel.activationAreasModal.tag.multiple`
    }),
    value: option
  }));

  return (
    <ModalUI
      className="activationAreasModal"
      closeModalCallback={onModalClose}
      leftButton={
        <ButtonUI
          disabled={false}
          onClick={onModalClose}
          text={formatMessage({
            defaultMessage: 'Cancel',
            id: 'actionToolsPanel.activationAreasModal.cancelButton'
          })}
          type="secondary"
        />
      }
      ref={modalRef}
      rightButton={
        <ButtonUI
          disabled={!selectedAction}
          onClick={() => onModalSave?.(selectedAction!)}
          text={formatMessage({
            defaultMessage: 'Next',
            id: 'actionToolsPanel.activationAreasModal.nextButton'
          })}
          type="primary"
        />
      }
      title={formatMessage({
        defaultMessage: 'Add Activation Area',
        id: 'actionToolsPanel.activationAreasModal.title'
      })}
      width="647px"
    >
      <div className="activationAreasModal__cardsContainer">
        {itemsConfig.map(item => (
          <Card
            className="activationAreasModal__actionCard"
            isSelected={item.value === selectedAction}
            key={item.value}
            onClick={() =>
              setState(prevState => ({
                ...prevState,
                selectedAction: item.value as ActivationAreasAction
              }))
            }
          >
            <Card.Header align="right">
              <div className="activationAreasModal__actionCardHeader">
                <Tag
                  className={cn('activationAreasModal__actionCardTag', {
                    'activationAreasModal__actionCardTag--selected': item.value === selectedAction
                  })}
                  id={`${item.value}-tag`}
                  status="information"
                >
                  {item.tag}
                </Tag>
                {item.value === 'addressRadiusFile' && (
                  <IconTooltip
                    customClassName="activationAreasModal__actionCardInfo"
                    event="mouseenter"
                    icon="fa-regular fa-info-circle"
                    iconColor={
                      item.value === selectedAction
                        ? 'var(--color-sonata-interaction-500) !important'
                        : 'var(--color-sonata-primary-500) !important'
                    }
                    place="top"
                  >
                    <p>
                      {formatMessage({
                        defaultMessage:
                          'While you may upload any address, we recommend verifying the areas to ensure accuracy. This prevents the placement of POIs in unintended locations, such as uninhabited regions or bodies of water.',
                        id: `actionToolsPanel.activationAreasModal.action.${item.value}.info`
                      })}
                    </p>
                  </IconTooltip>
                )}
              </div>
            </Card.Header>
            <Card.Media
              className={cn('activationAreasModal__actionCardIcon', {
                'activationAreasModal__actionCardIcon--selected': item.value === selectedAction
              })}
              icon={item.iconDefault}
            />
            <Card.Body align="center">
              <Card.Label title={item.label} variant="title">
                {item.label}
              </Card.Label>
              <Card.Label
                className="activationAreasModal__actionCardDescription"
                ellipsis={false}
                title={item.description}
                variant="subtitle"
              >
                {item.description}
              </Card.Label>
            </Card.Body>
          </Card>
        ))}
      </div>
    </ModalUI>
  );
}
